<script setup>
// Astra DS · SignalDot — el punto de emisión.
//
// Se enciende y se apaga mientras la radio suena, y se queda quieto y apagado
// cuando no. Encargo de Pablo, 29.08.26, en sustitución de la onda pequeña.
//
// 🔴 **Redondo, y es una excepción deliberada.** En esta casa el punto es
// cuadrado —el del masthead, el del marcador de sección, el de la leyenda de
// emisión— y así está escrito en tres componentes. Aquí se pide redondo a
// propósito: no es el punto de la marca, es la luz de «en el aire» de un
// estudio, que en un estudio es redonda.
//
// ── Por qué sustituye a la onda ──
//
// La onda leía el audio de verdad, y eso solo funciona en Chrome de escritorio.
// Medido el 29.08.26 con `?onda-check=1`: en Safari —de escritorio y de iPhone—
// el analizador devuelve cero aunque el contexto esté `running` y el audio
// entre por Media Source, porque WebKit no entrega las muestras. En el resto de
// navegadores la onda no se movía nunca, y una onda quieta se lee como avería.
//
// Un punto que parpadea no promete lo que no puede cumplir: no dice «así suena»,
// dice «esto está sonando», que es cierto en todas partes.
defineProps({
  // Si la emisión está sonando AHORA para quien mira. Apagado no es un fallo:
  // es que no se ha dado al play.
  playing: { type: Boolean, default: false },
});
</script>

<template>
  <!-- Decorativo: lo que dice ya lo dicen las palabras de al lado —«En emisión
       ahora», el nombre del programa, el estado de la cabecera—. Un lector de
       pantalla no gana nada oyendo hablar de un punto. -->
  <span
    class="signal-dot"
    :class="{ 'is-on': playing }"
    aria-hidden="true"
  />
</template>

<style scoped lang="scss">
.signal-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  flex: none;
  border-radius: 50%;
  background: currentColor;
  // Apagado no es invisible: el punto sigue estando, para que al encenderse se
  // entienda que es el mismo y no algo que aparece de la nada.
  opacity: 0.3;
}

// Por pasos y no por transición: una luz de estudio se enciende y se apaga, no
// respira. Es el mismo criterio que tenía el cuadrado de la cabecera, que es de
// donde viene esto.
.signal-dot.is-on {
  opacity: 1;
  animation: signal-dot-latido 1.6s steps(1) infinite;
}

@keyframes signal-dot-latido {
  0%,
  55% {
    opacity: 1;
  }
  56%,
  100% {
    opacity: 0.25;
  }
}

// Quien pidió no ver animaciones ve el punto encendido y quieto. El dato es que
// está sonando, y ese se conserva entero.
@media (prefers-reduced-motion: reduce) {
  .signal-dot.is-on {
    animation: none;
    opacity: 1;
  }
}
</style>
